import useChart from "../../../../Components/Chart/useChart";
import Chart from "../../../../Components/Chart/Chart";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { getFormatByTimescale, timeScalesData } from "../../../DashboardUtils";
import "./ExpectedWorkload.scss";
import { capitalize, groupBy } from "lodash-es";
import { DateTime, type DurationLikeObject } from "luxon";
import useSummary from "../../../useSummary";
import { statusCategories } from "../../../../Findings/FindingDetailsPane/FindingDetailsPaneHeader/Statuses/StatusUtils";
import { OVERDUE_FINDINGS } from "../SLAUtils";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import useWidgetFilterNavigator, {
  type FilterCondition,
} from "../../../useWidgetFilterNavigator";
import type { SelectedScope } from "../../../../types/scope";

// The supported time scales (keys of `timeScalesData`, which is defined in the
// still-untyped DashboardUtils JS module). Kept as a local literal union until
// a shared TimeScale type is available from the converted TimeScaleSelector.
type TimeScale = "DAY" | "WEEK" | "MONTH" | "QUARTER";

// A single summary row is the tuple [severityValue, slaRemainingTime, count].
type SummaryRow = [number, number, number];

// A filter condition as produced/consumed by the widget filter navigator.
interface WidgetFilterCondition {
  field: string;
  condition: string;
  value: unknown;
}

// A single bar's data point passed to amCharts. Severity keys are dynamic
// (one numeric value per severity), alongside the fixed `time`/`SLAFilter`.
interface ExpectedWorkloadDatum {
  time: string;
  SLAFilter?: WidgetFilterCondition[];
  [severity: string]: string | number | WidgetFilterCondition[] | undefined;
}

const BROKEN_SLA_FILTER: WidgetFilterCondition[] = [
  {
    field: "remediation_item.sla_status",
    condition: "in",
    value: ["BROKEN_SLA"],
  },
];

interface ExpectedWorkloadProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  timeScale: TimeScale;
  id: string;
  selector?: React.ReactNode;
  setIsReadyToExport: (isReady: boolean) => void;
}

const ExpectedWorkload: React.FC<ExpectedWorkloadProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  timeScale,
  id,
  selector = null,
  setIsReadyToExport,
}) => {
  const { getSeverityField, currentSeverityMapping } = useSeverityAsPriority();
  const chartName = `ExpectedWorkloadTrend-${id}`;
  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
    refetchSummary,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: ["finding.severity", "finding.sla_remaining_time"],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute || null,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_override: [
          JSON?.stringify({
            field: "remediation_item.actual_status",
            condition: "",
            value: {
              field: "category",
              condition: "in",
              value: [statusCategories.OPEN],
            },
          }),
        ],
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });
  const [dashboardData, setDashboardData] = useState<
    ExpectedWorkloadDatum[] | null
  >(null);
  const [dataExists, setChartDataExists] = useState<boolean | null>(null);
  const [dataError, setChartError] = useState(false);

  const startOfCurrentPeriod = DateTime.utc().startOf(
    timeScalesData?.[timeScale]?.luxonPeriod ?? "month"
  );

  const daysToSubtractFromCurrentPeriod = Math.floor(
    (
      DateTime.utc().diff(startOfCurrentPeriod, "days") as unknown as {
        values: Record<string, number>;
      }
    )?.values?.days as number
  );

  const { chartLoading, startLoadingChart, stopLoadingChart, createChart } =
    useChart(setIsReadyToExport);

  const clearData = () => {
    setDashboardData(null);
  };

  useEffect(() => {
    if (!!summaryLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [summaryLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!summaryDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [summaryDataError, setChartError, stopLoadingChart]);

  const getTimeScale = (timeValue: number): number => {
    if (timeValue < 0) {
      return -1;
    }

    const dateOfSLA = DateTime.utc().plus({
      [timeScalesData.DAY.luxonMathematicalUnit]: timeValue,
    } as DurationLikeObject);
    let nextPeriod = startOfCurrentPeriod.plus({
      [timeScalesData?.[timeScale]?.luxonMathematicalUnit ?? "months"]: 1,
    } as DurationLikeObject);

    let retVal = 0;
    while (dateOfSLA > nextPeriod) {
      retVal++;
      nextPeriod = nextPeriod.plus({
        [timeScalesData[timeScale]?.luxonMathematicalUnit]: 1,
      } as DurationLikeObject);
    }

    return retVal;
  };

  useEffect(() => {
    if (!!data) {
      const mappedData = (data as SummaryRow[])?.map((res) => {
        return {
          severity: capitalize(
            getSeverityField(res[0], SeverityFieldsEnum.NAME)
          ),
          time: getTimeScale(res[1]),
          value: res[2],
        };
      });

      const groupByTime = groupBy(mappedData, (item) => item.time);

      const groupKeysSorted = Object.keys(groupByTime).sort(
        (a, b) => Number(a) - Number(b)
      );

      const expectedWorkload: ExpectedWorkloadDatum[] = groupKeysSorted.map(
        (timeGroup, index) => {
          const timespanWorkload = groupByTime[timeGroup];
          const timespanWorkloadBySeverity = groupBy(
            timespanWorkload,
            (item) => item.severity
          );

          const dataObj: Record<string, number> = {};
          Object.keys(timespanWorkloadBySeverity)?.forEach((item) => {
            dataObj[item] = timespanWorkloadBySeverity[item].reduce(
              (sum, { value }) => sum + value,
              0
            );
          });

          let workloadTime;
          if (Number(timeGroup) >= 0) {
            workloadTime = startOfCurrentPeriod.plus({
              [timeScalesData?.[timeScale]?.luxonMathematicalUnit]:
                parseInt(timeGroup),
            } as DurationLikeObject);

            const min = Math.max(
              Math.floor(
                (
                  workloadTime.diff(
                    startOfCurrentPeriod,
                    "days"
                  ) as unknown as { values: Record<string, number> }
                )?.values?.days as number
              ) - daysToSubtractFromCurrentPeriod,
              0
            );

            const max =
              Math.floor(
                (
                  workloadTime.diff(
                    startOfCurrentPeriod,
                    "days"
                  ) as unknown as { values: Record<string, number> }
                )?.values?.days as number
              ) -
              daysToSubtractFromCurrentPeriod +
              Math.floor(
                (
                  workloadTime
                    .plus({
                      [timeScalesData?.[timeScale]?.luxonMathematicalUnit]: 1,
                    } as DurationLikeObject)
                    .diff(workloadTime, "days") as unknown as {
                    values: Record<string, number>;
                  }
                )?.values?.days as number
              );

            const SLAFilter: WidgetFilterCondition[] = [
              {
                field: "remediation_item.sla_remaining_time",
                condition: "gte",
                value: min,
              },
              {
                field: "remediation_item.sla_remaining_time",
                condition: "lt",
                value: max,
              },
            ];

            if (min === 0) {
              SLAFilter.push({
                field: "remediation_item.sla_status",
                condition: "not_in",
                value: ["BROKEN_SLA"],
              });
            }

            return {
              time: getFormatByTimescale(timeScale, workloadTime, index),
              SLAFilter,
              ...dataObj,
            };
          } else {
            return {
              time: OVERDUE_FINDINGS,
              ...dataObj,
            };
          }
        }
      );

      if (expectedWorkload.length === 0) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (expectedWorkload.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      setDashboardData(
        expectedWorkload?.slice(0, timeScalesData?.[timeScale]?.count)
      );
    }
  }, [data, timeScale]);

  const createCategoryAxis = (categoryAxis: am4charts.CategoryAxis) => {
    categoryAxis.dataFields.category = "time";
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 40;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.labels.template.fontWeight =
      400 as unknown as am4core.FontWeight;

    categoryAxis.renderer.labels.template.adapter.add(
      "fontWeight",
      (fontWeight, target) => {
        if (target?.currentText === OVERDUE_FINDINGS) {
          return 500 as unknown as am4core.FontWeight;
        }
        return fontWeight;
      }
    );

    categoryAxis.events.on("sizechanged", function (ev) {
      let axis = ev.target;
      const cellWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex);
      if (cellWidth < 146) {
        axis.renderer.labels.template.wrap = true;
        categoryAxis.renderer.labels.template.maxWidth = 68;
        categoryAxis.renderer.labels.template.fontSize = 12;
      } else {
        categoryAxis.renderer.labels.template.maxWidth = 210;
      }
    });
  };

  const createValueAxis = (valueAxis: am4charts.ValueAxis) => {
    valueAxis.min = 0;
    valueAxis.strictMinMax = true;
    valueAxis.renderer.labels.template.fontSize = 12;
    valueAxis.paddingLeft = 10;
    valueAxis.paddingRight = 20;
    valueAxis.layout = "absolute";
    valueAxis.title.text = "Findings";
    valueAxis.title.fontSize = 12;
    valueAxis.title.fontWeight = 500 as unknown as am4core.FontWeight;
    valueAxis.title.rotation = 0;
    valueAxis.title.dy = -30;
    valueAxis.title.align = "center";
    valueAxis.title.valign = "top";
  };

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);
      chart.data = dashboardData ?? [];
      chart.paddingTop = 45;
      chart.paddingRight = 40;

      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      createCategoryAxis(categoryAxis);
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis);

      const range = categoryAxis.axisRanges.create();
      const overdueFindingsExist =
        dashboardData?.[0]?.time === OVERDUE_FINDINGS;

      if (overdueFindingsExist) {
        range.category = OVERDUE_FINDINGS;
        range.grid.strokeWidth = 3;
        range.axisFill.fillOpacity = 0.2;
        range.axisFill.fill = am4core.color("#7F5FDC");
        range.axisFill.stroke = am4core.color("#7F5FDC");
        range.axisFill.strokeDasharray = "3,3";
      }

      chart.legend.useDefaultMarker = true;
      let markerTemplate = chart.legend.markers.template;
      markerTemplate.width = 12;
      markerTemplate.height = 12;
      markerTemplate.strokeOpacity = 0;

      const chartClick = (
        severity: number | undefined,
        SLARemainingTime: WidgetFilterCondition[]
      ) => {
        const mappedForFilterClick = [
          {
            field: "remediation_item.actual_status",
            condition: "in",
            value: [
              {
                type: "category",
                key: statusCategories.OPEN,
                id: statusCategories.OPEN,
              },
            ],
          },
          ...SLARemainingTime,
          {
            value: [severity],
            field: "remediation_item.severity",
            condition: "in",
          },
        ].map((filterItem) => [
          filterItem.condition,
          filterItem.field,
          filterItem.value,
        ]);

        onFilterClick(mappedForFilterClick as unknown as FilterCondition[]);
      };

      const createSeries = (field: string, color: string) => {
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.name = field;
        series.dataFields.valueY = field;
        series.dataFields.categoryX = "time";
        series.sequencedInterpolation = true;

        series.stacked = true;
        series.fill = am4core.color(color);
        series.columns.template.width = am4core.percent(60);
        series.columns.template.maxWidth = 80;
        series.columns.template.tooltipText =
          "[bold text-transform: capitalize]{name}[/]\n[font-size:14px]{categoryX}: {valueY}";
        series.tooltip!.autoTextColor = false;
        series.columns.template.strokeOpacity = 1;
        // amCharts types `strokeWidth` as number; the original assigns the
        // string "2px". Preserve runtime behavior with a cast.
        series.columns.template.strokeWidth = "2px" as unknown as number;
        series.cursorOverStyle = am4core.MouseCursorStyle.pointer;

        series.columns.template.events.on("hit", (e) => {
          const clickedSeverity = currentSeverityMapping?.find(
            (s) => s.name.toLowerCase() === field.toLowerCase()
          )?.value;

          const dataContext = e.target.dataItem
            ?.dataContext as ExpectedWorkloadDatum;
          const clickedCategory = dataContext.time;
          let SLAFilterToExecute: WidgetFilterCondition[];
          if (clickedCategory === OVERDUE_FINDINGS) {
            SLAFilterToExecute = BROKEN_SLA_FILTER;
          } else {
            const { SLAFilter } = dataContext;
            // `SLAFilter` is always populated for non-overdue data points
            // (set above for every `timeGroup >= 0`).
            SLAFilterToExecute = SLAFilter as WidgetFilterCondition[];
          }

          chartClick(clickedSeverity, SLAFilterToExecute);
        });

        return series;
      };

      currentSeverityMapping?.forEach((item) => {
        createSeries(capitalize(item.name), item.color);
      });

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [dashboardData, dataExists, createChart]);

  return (
    <>
      {!!selector && (
        <div className={"expected-workload-timescale-selection"}>
          {selector}
        </div>
      )}
      <Chart
        chartLoading={chartLoading}
        chartName={chartName}
        chartError={dataError}
        chartData={dataExists}
        chartReload={refetchSummary}
      />
    </>
  );
};

export default ExpectedWorkload;
